import React, { useState, useEffect, useRef, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useChatAI } from "../chat-ai-context";
import { useQuoteContext } from "../hooks/use-quote-context";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { Avatar } from "@/components/base/avatar/avatar";
import { quoteService } from "@/data/mock-quotes";

interface QuoteResponseWizardProps {
    onCancel: () => void;
}

type Step = "quoteRequest" | "responseSubject" | "responseBody" | "review" | "completed";

// Componente memoizzato per i messaggi dell'assistente
const AssistantMessage = memo(({ question, avatar }: { question: string; avatar: React.ReactElement }) => {
    return (
        <div className="flex items-start">
            {avatar}
            <div className="flex-1">
                <div className="max-w-[520px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                    {question}
                </div>
            </div>
        </div>
    );
});

AssistantMessage.displayName = "AssistantMessage";

export const QuoteResponseWizard = ({ onCancel }: QuoteResponseWizardProps) => {
    const { t } = useTranslation(['common', 'pages:quotes']);
    const navigate = useNavigate();
    const { wizardFormData, setWizardFormData, appendMessage, quoteRequestId, setQuoteRequestId, closeDrawer } = useChatAI();
    const quoteContext = useQuoteContext();
    const listRef = useRef<HTMLDivElement | null>(null);

    const [currentStep, setCurrentStep] = useState<Step>("quoteRequest");
    const [quoteOptions, setQuoteOptions] = useState<Array<{ id: string; label: string }>>([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Se c'è un contesto quote, usalo direttamente
        if (quoteContext) {
            setQuoteRequestId(quoteContext.id);
            setCurrentStep("responseSubject");
            appendMessage({ role: "assistant", content: t('aiChat.wizard.quoteResponse.usingContext', { subject: quoteContext.subject }) });
            setTimeout(() => {
                appendMessage({ role: "assistant", content: t('aiChat.wizard.quoteResponse.responseSubject') });
            }, 500);
        } else {
            // Carica tutte le quote requests disponibili
            const allQuotes = quoteService.getAll();
            const options = allQuotes.map(quote => ({
                id: quote.id,
                label: `${quote.subject} - ${quote.fromName || quote.from}`,
            }));
            setQuoteOptions(options);
        }
    }, [quoteContext]);

    useEffect(() => {
        if (wizardFormData[currentStep]) {
            setInputValue(String(wizardFormData[currentStep]));
        } else {
            setInputValue("");
        }
    }, [currentStep, wizardFormData]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    }, [currentStep]);

    const getStepQuestion = (step: Step): string => {
        switch (step) {
            case "quoteRequest":
                return t('aiChat.wizard.quoteResponse.selectQuote');
            case "responseSubject":
                return t('aiChat.wizard.quoteResponse.responseSubject');
            case "responseBody":
                return t('aiChat.wizard.quoteResponse.responseBody');
            case "review":
                // Genera il messaggio di review con i dati inseriti
                const quote = quoteRequestId ? quoteService.getById(quoteRequestId) : null;
                return t('aiChat.wizard.quoteResponse.review', {
                    quoteSubject: quote?.subject || t('aiChat.wizard.notSpecified'),
                    responseSubject: wizardFormData.responseSubject || '',
                    responseBody: wizardFormData.responseBody || '',
                });
            default:
                return "";
        }
    };

    const handleAnswer = (value: string) => {
        const updatedData = { ...wizardFormData, [currentStep]: value };
        setWizardFormData(updatedData);

        appendMessage({ role: "user", content: value });

        setTimeout(() => {
            appendMessage({ role: "assistant", content: t('aiChat.wizard.confirmed') });

            const steps: Step[] = ["quoteRequest", "responseSubject", "responseBody", "review"];
            const currentIndex = steps.indexOf(currentStep);
            if (currentIndex < steps.length - 1) {
                setTimeout(() => {
                    setCurrentStep(steps[currentIndex + 1]);
                    appendMessage({ role: "assistant", content: getStepQuestion(steps[currentIndex + 1]) });
                }, 500);
            } else {
                setTimeout(() => {
                    setCurrentStep("review");
                    showReview();
                }, 500);
            }
        }, 500);
    };

    const handleQuoteSelect = (quoteId: string) => {
        setQuoteRequestId(quoteId);
        handleAnswer(quoteId);
    };

    const showReview = () => {
        const quote = quoteRequestId ? quoteService.getById(quoteRequestId) : null;
        const reviewMessage = t('aiChat.wizard.quoteResponse.review', {
            quoteSubject: quote?.subject || t('aiChat.wizard.notSpecified'),
            responseSubject: wizardFormData.responseSubject,
            responseBody: wizardFormData.responseBody,
        });
        appendMessage({ role: "assistant", content: reviewMessage });
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            if (!quoteRequestId || !wizardFormData.responseSubject?.trim() || !wizardFormData.responseBody?.trim()) {
                appendMessage({ role: "assistant", content: t('form.errors.allFieldsRequired', { ns: 'common' }) });
                setIsLoading(false);
                return;
            }

            // Update quote request with response
            const quote = quoteService.getById(quoteRequestId);
            if (quote) {
                quoteService.update(quoteRequestId, {
                    responseSubject: wizardFormData.responseSubject,
                    responseBody: wizardFormData.responseBody,
                    status: 'responded',
                    respondedAt: new Date(),
                });
            }

            appendMessage({ role: "assistant", content: t('aiChat.wizard.quoteResponse.success') });
            setCurrentStep("completed");

            setTimeout(() => {
                closeDrawer();
                // Naviga alla pagina di dettaglio della quote creata
                navigate(`/freight-forwarder/quotes/${quoteRequestId}`);
            }, 2000);
        } catch (error) {
            appendMessage({ role: "assistant", content: t('form.errors.generic', { ns: 'common' }) });
        } finally {
            setIsLoading(false);
        }
    };

    const renderInput = () => {
        if (currentStep === "review") {
            return (
                <div className="space-y-3">
                    <Button onClick={handleSubmit} color="primary" size="lg" isDisabled={isLoading}>
                        {isLoading ? t('form.saving', { ns: 'common' }) : t('aiChat.wizard.create')}
                    </Button>
                    <Button onClick={onCancel} color="secondary" size="lg">
                        {t('buttons.cancel')}
                    </Button>
                </div>
            );
        }

        if (currentStep === "completed") {
            return (
                <div className="text-center text-primary">
                    {t('aiChat.wizard.quoteResponse.completed')}
                </div>
            );
        }

        if (currentStep === "quoteRequest") {
            return (
                <div className="space-y-3">
                    <div className="space-y-2">
                        {quoteOptions.map((option) => (
                            <Button
                                key={option.id}
                                onClick={() => handleQuoteSelect(option.id)}
                                color="secondary"
                                size="lg"
                                className="w-full"
                            >
                                {option.label}
                            </Button>
                        ))}
                    </div>
                    <Button onClick={onCancel} color="secondary" size="md">
                        {t('buttons.cancel')}
                    </Button>
                </div>
            );
        }

        if (currentStep === "responseBody") {
            return (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (inputValue.trim()) {
                            handleAnswer(inputValue.trim());
                            setInputValue("");
                        }
                    }}
                    className="space-y-3"
                >
                    <TextArea
                        value={inputValue}
                        onChange={setInputValue}
                        placeholder={t('aiChat.wizard.enterValue')}
                        rows={8}
                    />
                    <div className="flex gap-2">
                        <Button type="submit" color="primary" size="md">
                            {t('buttons.next')}
                        </Button>
                        <Button type="button" onClick={onCancel} color="secondary" size="md">
                            {t('buttons.cancel')}
                        </Button>
                    </div>
                </form>
            );
        }

        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (inputValue.trim()) {
                        handleAnswer(inputValue.trim());
                        setInputValue("");
                    }
                }}
                className="space-y-3"
            >
                <Input
                    value={inputValue}
                    onChange={setInputValue}
                    placeholder={t('aiChat.wizard.enterValue')}
                    size="md"
                />
                <div className="flex gap-2">
                    <Button type="submit" color="primary" size="md">
                        {t('buttons.next')}
                    </Button>
                    <Button type="button" onClick={onCancel} color="secondary" size="md">
                        {t('buttons.cancel')}
                    </Button>
                </div>
            </form>
        );
    };

    // Memoizzare l'avatar per evitare che cambi ad ogni render
    const assistantAvatarUrl = useMemo(() => {
        const avatarPool = [
            "https://i.pravatar.cc/80?img=1",
            "https://i.pravatar.cc/80?img=2",
        ];
        return avatarPool[Math.floor(Math.random() * avatarPool.length)];
    }, []); // Solo una volta al mount

    // Memoizzare il componente avatar per evitare re-render inutili
    const AssistantAvatar = useMemo(() => {
        return <Avatar size="sm" src={assistantAvatarUrl} status="online" className="mr-3" />;
    }, [assistantAvatarUrl]);

    // Memoizzare la domanda dello step corrente per evitare re-render quando cambia solo inputValue
    // Per lo step "review", includiamo anche wizardFormData e quoteRequestId nelle dipendenze
    const currentStepQuestion = useMemo(() => {
        return getStepQuestion(currentStep);
    }, [currentStep, t, wizardFormData, quoteRequestId]);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div ref={listRef} className="flex-1 overflow-y-auto py-4 md:py-6">
                <div className="flex flex-col gap-5">
                    {/* Mostra il messaggio dell'assistente per tutti gli step, incluso review */}
                    {(!quoteContext || currentStep === "review") && currentStepQuestion && (
                        <AssistantMessage 
                            question={currentStepQuestion}
                            avatar={AssistantAvatar}
                        />
                    )}

                    {renderInput()}
                </div>
            </div>
        </div>
    );
};

